import React from 'react'
import { Image, Page, Text, View } from '@react-pdf/renderer'
import moment from 'moment'

// Shared building blocks for AmbuNet report PDFs (Post Event Report, fleet
// and availability print-outs), in the same look as the CRM safety incident
// and hazard log PDFs (navy/teal, hairline rules, uppercase micro-labels).
// Portrait A4: content width is 535pt.

export const NAVY = '#123F4A'
export const TEAL = '#239AA4'
export const MUTED = '#64748B'
export const LINE = '#E2E8F0'
export const INK = '#334155'
export const FAINT = '#94A3B8'
export const TINT = '#F8FAFC'
export const GOOD = '#27803B'
export const WARN = '#B54708'
export const BAD = '#B42318'

// Helvetica (the built-in PDF font) only covers Latin-1, so swap the symbols
// free text tends to contain for ASCII and drop anything else rather than
// print tofu.
export const clean = (value) =>
  String(value ?? '')
    .replace(/→/g, '->')
    .replace(/←/g, '<-')
    .replace(/≥/g, '>=')
    .replace(/≤/g, '<=')
    .replace(/≠/g, '!=')
    .replace(/[—–]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, '...')
    .replace(/•/g, '-')
    .replace(/[^\x00-\xFF]/g, '')

export const fmtDate = (value) => (value ? moment(value).format('D MMM YYYY') : '')
export const fmtDateTime = (value) =>
  value ? moment(value).format('D MMM YYYY HH:mm') : ''
export const fmtDayKey = (key, long = false) =>
  moment(key, 'YYYY-MM-DD').format(long ? 'dddd D MMMM YYYY' : 'ddd D MMM')

// Minutes as "7.5 min" / "1 h 20 min"; "-" when not measurable
export const fmtMinutes = (value) => {
  if (value === null || value === undefined) return '-'
  if (value < 60) return `${value} min`
  const h = Math.floor(value / 60)
  const m = Math.round(value % 60)
  return m ? `${h} h ${m} min` : `${h} h`
}

export const fmtPct = (value) =>
  value === null || value === undefined ? '-' : `${value}%`

export const fmtHour = (hour) =>
  hour === null || hour === undefined ? '-' : `${String(hour).padStart(2, '0')}:00`

export const PAGE_STYLE = {
  paddingTop: 64,
  paddingBottom: 48,
  paddingHorizontal: 30,
  fontFamily: 'Helvetica',
  fontSize: 8,
  color: '#0F172A',
}

// Sort { label: count } into [[label, count]] descending, folding the tail
// into "Other" so long lists stay readable
export const topEntries = (counts = {}, limit = 10) => {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  if (entries.length <= limit) return entries
  const head = entries.slice(0, limit - 1)
  const rest = entries.slice(limit - 1).reduce((s, [, v]) => s + v, 0)
  return [...head, ['Other', rest]]
}

export const sumValues = (counts = {}) => Object.values(counts).reduce((a, b) => a + b, 0)

// ---------------------------------------------------------------------------

export const Pill = ({ label, color }) => (
  <View
    style={{
      backgroundColor: color || '#F1F5F9',
      borderRadius: 3,
      paddingVertical: 1.5,
      paddingHorizontal: 4,
      alignSelf: 'flex-start',
    }}
  >
    <Text
      style={{
        fontSize: 6.5,
        color: color ? '#FFFFFF' : MUTED,
        fontFamily: 'Helvetica-Bold',
      }}
    >
      {clean(label)}
    </Text>
  </View>
)

// Big numbered heading that opens a section page
export const SectionHeading = ({ number, title, intro }) => (
  <View style={{ marginBottom: 8 }} minPresenceAhead={80}>
    <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
      {number ? (
        <Text
          style={{
            fontSize: 20,
            fontFamily: 'Helvetica-Bold',
            color: TEAL,
            marginRight: 8,
          }}
        >
          {number}
        </Text>
      ) : null}
      <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: NAVY }}>
        {clean(title)}
      </Text>
    </View>
    <View style={{ height: 2, backgroundColor: TEAL, width: 40, marginTop: 5 }} />
    {intro ? (
      <Text style={{ fontSize: 7.5, color: MUTED, marginTop: 6, lineHeight: 1.4 }}>
        {clean(intro)}
      </Text>
    ) : null}
  </View>
)

export const SectionTitle = ({ children, marginTop = 14 }) => (
  <Text
    style={{
      fontSize: 10.5,
      color: NAVY,
      fontFamily: 'Helvetica-Bold',
      marginTop,
      marginBottom: 5,
    }}
    minPresenceAhead={60}
  >
    {clean(children)}
  </Text>
)

export const StatBox = ({ label, value, sub, color, last }) => (
  <View
    style={{
      flex: 1,
      borderWidth: 0.5,
      borderColor: LINE,
      borderRadius: 4,
      padding: 7,
      marginRight: last ? 0 : 6,
    }}
  >
    <Text
      style={{
        fontSize: 6,
        color: MUTED,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      {clean(label)}
    </Text>
    <Text
      style={{
        fontSize: 16,
        color: color || NAVY,
        fontFamily: 'Helvetica-Bold',
        marginTop: 2,
      }}
    >
      {clean(value)}
    </Text>
    {sub ? (
      <Text style={{ fontSize: 6.5, color: MUTED, marginTop: 1 }}>{clean(sub)}</Text>
    ) : null}
  </View>
)

export const StatRow = ({ items }) => (
  <View style={{ flexDirection: 'row', marginBottom: 6 }} wrap={false}>
    {items.map((item, i) => (
      <StatBox key={item.label} {...item} last={i === items.length - 1} />
    ))}
  </View>
)

// Label/value rows with an optional proportion bar
// Sits in a column (see Columns), so it takes its parent's width; no flex
// here, which in a column parent would collapse it to zero height
export const CountTable = ({ title, rows, total, emptyText = 'None recorded' }) => (
  <View>
    {title ? (
      <Text
        style={{
          fontSize: 7.5,
          color: NAVY,
          fontFamily: 'Helvetica-Bold',
          marginBottom: 3,
        }}
      >
        {clean(title)}
      </Text>
    ) : null}
    {rows.length === 0 ? (
      <Text style={{ fontSize: 7, color: MUTED }}>{emptyText}</Text>
    ) : (
      rows.map(([label, value]) => (
        <View
          key={label}
          style={{
            paddingVertical: 2,
            borderBottomWidth: 0.5,
            borderBottomColor: LINE,
          }}
          wrap={false}
        >
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: 7, color: INK, flex: 1, paddingRight: 4 }}>
              {clean(label)}
            </Text>
            <Text style={{ fontSize: 7, fontFamily: 'Helvetica-Bold' }}>
              {value}
              {total ? (
                <Text style={{ fontFamily: 'Helvetica', color: MUTED }}>
                  {`  ${Math.round((value / total) * 100)}%`}
                </Text>
              ) : null}
            </Text>
          </View>
          {total ? (
            <View style={{ height: 2, backgroundColor: '#EEF2F6', marginTop: 1.5, borderRadius: 1 }}>
              <View
                style={{
                  height: 2,
                  width: `${Math.max(1, Math.round((value / total) * 100))}%`,
                  backgroundColor: TEAL,
                  borderRadius: 1,
                }}
              />
            </View>
          ) : null}
        </View>
      ))
    )}
  </View>
)

// Navy column-header band. Pass `fixed` so it repeats on every page of a
// long table (DataTable's inline header does not).
export const TableHeader = ({ columns, fixed = false }) => (
  <View
    style={{
      flexDirection: 'row',
      backgroundColor: NAVY,
      paddingVertical: 4,
      paddingHorizontal: 3,
      borderRadius: 3,
    }}
    wrap={false}
    fixed={fixed}
  >
    {columns.map((col) => (
      <Text
        key={col.label}
        style={{
          flex: col.flex || 1,
          paddingHorizontal: 3,
          fontSize: 6,
          color: '#FFFFFF',
          fontFamily: 'Helvetica-Bold',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          textAlign: col.align || 'left',
        }}
      >
        {clean(col.label)}
      </Text>
    ))}
  </View>
)

// columns: [{ label, flex, align }]; rows: arrays of cell values
export const DataTable = ({ columns, rows, emptyText = 'Nothing recorded', boldFirst = true }) => (
  <View>
    <TableHeader columns={columns} />
    {rows.length === 0 ? (
      <Text style={{ fontSize: 7, color: MUTED, padding: 5 }}>{emptyText}</Text>
    ) : (
      rows.map((row, r) => (
        <View
          key={r}
          style={{
            flexDirection: 'row',
            paddingVertical: 3.5,
            paddingHorizontal: 3,
            backgroundColor: r % 2 ? TINT : undefined,
            borderBottomWidth: 0.5,
            borderBottomColor: LINE,
          }}
          wrap={false}
        >
          {row.map((cell, c) => (
            <Text
              key={c}
              style={{
                flex: columns[c]?.flex || 1,
                paddingHorizontal: 3,
                fontSize: 7,
                color: INK,
                textAlign: columns[c]?.align || 'left',
                fontFamily: c === 0 && boldFirst ? 'Helvetica-Bold' : 'Helvetica',
              }}
            >
              {clean(cell ?? '-')}
            </Text>
          ))}
        </View>
      ))
    )}
  </View>
)

export const Label = ({ children }) => (
  <Text
    style={{
      fontSize: 6,
      color: MUTED,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginTop: 5,
      marginBottom: 1.5,
      fontFamily: 'Helvetica-Bold',
    }}
  >
    {children}
  </Text>
)

// A free-text paragraph; blank lines become paragraph breaks
export const Prose = ({ text, size = 8 }) =>
  String(text ?? '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p, i) => (
      <Text key={i} style={{ fontSize: size, color: INK, lineHeight: 1.45, marginBottom: 4 }}>
        {clean(p)}
      </Text>
    ))

export const Callout = ({ title, children, tone = TEAL }) => (
  <View
    style={{
      borderLeftWidth: 2.5,
      borderLeftColor: tone,
      backgroundColor: TINT,
      paddingVertical: 6,
      paddingHorizontal: 8,
      marginVertical: 6,
    }}
    wrap={false}
  >
    {title ? (
      <Text style={{ fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: NAVY, marginBottom: 2 }}>
        {clean(title)}
      </Text>
    ) : null}
    <Text style={{ fontSize: 7, color: INK, lineHeight: 1.4 }}>{children}</Text>
  </View>
)

// A quoted comment card
export const Quote = ({ text, meta }) => (
  <View
    style={{
      borderWidth: 0.5,
      borderColor: LINE,
      borderRadius: 4,
      padding: 7,
      marginTop: 5,
    }}
  >
    <Text style={{ fontSize: 7.5, color: INK, lineHeight: 1.4, fontFamily: 'Helvetica-Oblique' }}>
      "{clean(text)}"
    </Text>
    {meta ? <Text style={{ fontSize: 6.5, color: MUTED, marginTop: 3 }}>{clean(meta)}</Text> : null}
  </View>
)

export const ChartImage = ({ image, caption }) =>
  image ? (
    <View style={{ marginVertical: 4 }} wrap={false}>
      <Image src={image.src} style={{ width: image.width, height: image.height }} />
      {caption ? (
        <Text style={{ fontSize: 6.5, color: MUTED, marginTop: 2 }}>{clean(caption)}</Text>
      ) : null}
    </View>
  ) : null

export const Columns = ({ children, gap = 12 }) => {
  const items = React.Children.toArray(children).filter(Boolean)
  return (
    <View style={{ flexDirection: 'row' }}>
      {items.map((child, i) => (
        <View key={i} style={{ flex: 1, marginRight: i === items.length - 1 ? 0 : gap }}>
          {child}
        </View>
      ))}
    </View>
  )
}

// Two half-width blocks per row; a lone block runs full width
export const InPairs = ({ blocks }) => {
  const shown = blocks.filter(Boolean)
  const rows = []
  for (let i = 0; i < shown.length; i += 2) rows.push(shown.slice(i, i + 2))
  return rows.map((pair, i) => (
    <Columns key={i}>
      {pair.map((block, j) => (
        <View key={j}>{block}</View>
      ))}
    </Columns>
  ))
}

// Running header on every content page. `docLabel` is the uppercase
// micro-label naming the document type; `title` names this document.
export const Header = ({ orgName, logo, docLabel, title }) => (
  <View
    style={{
      position: 'absolute',
      top: 22,
      left: 30,
      right: 30,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: 6,
      borderBottomWidth: 0.5,
      borderBottomColor: LINE,
    }}
    fixed
  >
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      {logo ? (
        <Image src={logo} style={{ height: 20, maxWidth: 80, objectFit: 'contain', marginRight: 7 }} />
      ) : null}
      <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: NAVY }}>
        {clean(orgName)}
      </Text>
    </View>
    <View style={{ alignItems: 'flex-end' }}>
      <Text style={{ fontSize: 6.5, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {clean(docLabel)}
      </Text>
      <Text style={{ fontSize: 7, color: INK, maxWidth: 300, textAlign: 'right' }}>
        {clean(title)}
      </Text>
    </View>
  </View>
)

export const Footer = ({ orgName, docLabel, title, generatedAt, generatedBy }) => (
  <View
    style={{
      position: 'absolute',
      bottom: 16,
      left: 30,
      right: 30,
      paddingTop: 5,
      borderTopWidth: 0.5,
      borderTopColor: LINE,
      flexDirection: 'row',
      justifyContent: 'space-between',
    }}
    fixed
  >
    <View style={{ flex: 1, paddingRight: 8 }}>
      <Text style={{ fontSize: 6, color: FAINT }}>
        {clean([orgName, docLabel, title, 'CONFIDENTIAL'].filter(Boolean).join(' - '))}
      </Text>
      <Text style={{ fontSize: 6, color: FAINT, marginTop: 1 }}>
        {clean(
          `Generated ${fmtDateTime(generatedAt)}${generatedBy ? ` by ${generatedBy}` : ''} - Powered by AmbuNet`,
        )}
      </Text>
    </View>
    <Text
      style={{ fontSize: 6.5, color: FAINT }}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
  </View>
)

// A content page: running header and footer around the section body.
// `chrome` = { orgName, logo, docLabel, title, generatedAt, generatedBy }
export const ReportPage = ({ chrome, orientation, children }) => (
  <Page size="A4" orientation={orientation} style={PAGE_STYLE}>
    <Header
      orgName={chrome.orgName}
      logo={chrome.logo}
      docLabel={chrome.docLabel}
      title={chrome.title}
    />
    {children}
    <Footer
      orgName={chrome.orgName}
      docLabel={chrome.docLabel}
      title={chrome.title}
      generatedAt={chrome.generatedAt}
      generatedBy={chrome.generatedBy}
    />
  </Page>
)
